import React from 'react';
import { Link } from 'react-router-dom';
import { BoltIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { preloadHubSpotFormsScript } from '../../services/HubSpotForms';

// Homepage-only CTA card pointing at the AWS "sign up for Amazon Quick Suite
// free tier" flow (see site.js's `quickTry` block for the copy/URL). Sits in
// the exact same hero-right-column slot that VideoEmbed's `chrome` mode
// occupies on every individual use-case page (see UseCaseView/Home), and is
// deliberately built with a similar "tilted 3D card" treatment -- rounded-xl,
// shadow-2xl, a perspective tilt that straightens out on hover -- so it
// reads as the same family of component instead of a one-off banner, minus
// the thick black border a chrome VideoEmbed has (removed so the card reads
// as a lighter, more see-through pane rather than a bordered frame). No
// caption strip below it either -- the card's own heading/button already
// say what it is.
// Unlike VideoEmbed, there's no "hasStarted" playback state to flatten the
// tilt on, so this flattens on hover/focus instead (a link, not a video).
const QuickTryCard = ({
  title,
  description,
  buttonLabel,
  url,
  className = '',
  aspectClassName = 'aspect-[16/7.8]',
  icon: Icon = BoltIcon,
}) => {
  if (!url) {
    return null;
  }

  // A site-relative url (e.g. "/contact") stays in the SPA via react-router;
  // anything else is treated as external and opens in a new tab.
  const isInternal = url.startsWith('/');
  const LinkTag = isInternal ? Link : 'a';
  const linkProps = isInternal
    ? {
        to: url,
        // Same HubSpot preload the hero "Talk to our team" button uses.
        ...(url === '/contact' && {
          onMouseEnter: preloadHubSpotFormsScript,
          onTouchStart: preloadHubSpotFormsScript,
        }),
      }
    : { href: url, target: '_blank', rel: 'noopener noreferrer' };

  return (
    <div className={className}>
      <LinkTag
        {...linkProps}
        aria-label={buttonLabel || title}
        // Opens in a new tab (rel=noopener noreferrer since this is an
        // external AWS domain) rather than navigating the demo site away --
        // someone scanning the site's own QR code mid-demo shouldn't lose
        // their place on the homepage just to sign up for Quick.
        className="group block origin-top overflow-hidden rounded-xl shadow-2xl ring-1 ring-white/20 transition-transform duration-500 ease-out [transform:perspective(1600px)_rotateX(6deg)_rotateY(-18deg)] hover:[transform:perspective(1600px)_rotateX(0deg)_rotateY(0deg)] focus-visible:[transform:perspective(1600px)_rotateX(0deg)_rotateY(0deg)]"
      >
        <div
          className={`relative flex ${aspectClassName} flex-col items-center justify-center overflow-hidden text-center`}
        >
          {/* No painted-on haze of its own anymore -- this card has no
              background layer at all now, so the translucent panel below is
              a real "see-through" window onto whatever's actually behind it
              on the page (the hero section's own HeroSwoopBackground swoosh
              on Home/UseCaseView), not a separate color effect this
              component was drawing for itself. Opacity matched exactly to
              white/90 -- the same mobile hero title card treatment used on
              every other page (see UseCaseView/Home) -- instead of an
              opacity picked just for this card, so it reads as the same
              "see-through card" everywhere on the site. No backdrop-blur
              either, matching that same pattern (those cards rely on the
              swoosh graphic underneath already being blurred, not on
              blurring it again here). */}
          <div className="relative flex h-full w-full flex-col items-center justify-center gap-3 bg-white/90 px-6 py-5 sm:px-10">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-50 ring-1 ring-primary-100">
              <Icon className="h-7 w-7 text-primary-600" aria-hidden="true" />
            </span>

            <h3 className="text-xl font-bold text-gray-900 sm:text-2xl">
              {title}
            </h3>

            {description && (
              <p className="max-w-sm text-sm text-gray-600">{description}</p>
            )}

            <span className="mt-2 inline-flex items-center gap-2 rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition group-hover:bg-primary-700">
              {buttonLabel}
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
            </span>
          </div>
        </div>
      </LinkTag>
    </div>
  );
};

export default QuickTryCard;
